/**
 * Tipos para DERAG10R2Service — Infraestructura energética y medioambiental
 *
 * Datos: DERA G10 (14 capas puntuales) — IECA Junta de Andalucía
 * Incluye: EDAR, ETAP, subestaciones, plantas solares, eólicos, RSU, puntos limpios…
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /dera-g10/{ine5}.json.gz
 *
 * @module types/deraG10
 * @version 1.0.0
 */

// =============================================================================
// FEATURE — Esquema común DERAFeaturePTEL (generado por dera-g10-download.ts)
// =============================================================================

/** Feature G10 almacenada en R2 */
export interface DERAG10Feature {
  /** ID DERA original */
  id: string;
  /** Nombre de la infraestructura */
  nombre: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Código INE municipio (5 dígitos) */
  cod_ine: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Capa DERA de origen (ej: "g10_27_Edar") */
  capa: string;
  /** Subtipo PTEL (ej: "EDAR", "ETAP", "SUBESTACION") */
  subtipo: string;
  /** Metadatos extra (potencia, tecnología, etc.) */
  meta?: Record<string, string | number>;
  /**
   * EDAR-b (1-nov-2026): atributos de la capa tal como llegan de R2 (`/dera-g10/{ine}`):
   * `situacion` (paraje) y `procedenci` en EDAR; `tipo` («Depósitos», «ETAP clorador»,
   * «ETAP desaladora»…) en ETAP. Medido en vivo: R2 trae `metadata`, `codMun` y `capaOrigen`;
   * `meta`, `cod_ine` y `capa` de este tipo no llegan (desajuste catalogado aparte).
   */
  metadata?: Record<string, string | number | null>;
}

// =============================================================================
// SUBTIPOS G10
// =============================================================================

/** Subtipos de infraestructura G10 */
export type DERAG10Subtipo =
  | 'CENTRAL_HIDROELECTRICA'
  | 'PARQUE_EOLICO'
  | 'PLANTA_SOLAR'
  | 'BIOMASA'
  | 'BIOGAS'
  | 'COGENERACION'
  | 'TERMICA'
  | 'SUBESTACION'
  | 'EQUIP_NATURALEZA'
  | 'INFOCA'
  | 'EDAR'
  | 'RSU'
  | 'ETAP'
  | 'PUNTO_LIMPIO';

/** Mapeo subtipo → tipología PTEL */
export const G10_SUBTIPO_TO_TIPOLOGIA: Record<DERAG10Subtipo, string> = {
  CENTRAL_HIDROELECTRICA: 'ENERGIA',
  PARQUE_EOLICO: 'ENERGIA',
  PLANTA_SOLAR: 'ENERGIA',
  BIOMASA: 'ENERGIA',
  BIOGAS: 'ENERGIA',
  COGENERACION: 'ENERGIA',
  TERMICA: 'ENERGIA',
  SUBESTACION: 'ENERGIA',
  EQUIP_NATURALEZA: 'MEDIOAMBIENTE',
  INFOCA: 'MEDIOAMBIENTE',
  EDAR: 'SANEAMIENTO',
  RSU: 'RESIDUOS',
  ETAP: 'ABASTECIMIENTO',
  PUNTO_LIMPIO: 'RESIDUOS',
};

// =============================================================================
// OPCIONES Y RESULTADOS
// =============================================================================

/** Opciones para búsqueda en G10 R2 */
export interface DERAG10SearchOptions {
  /** Código INE municipio (5 dígitos) — REQUERIDO */
  codINE: string;
  /** Nombre infraestructura para fuzzy match */
  name?: string;
  /** Filtrar por subtipo G10 */
  subtipo?: string;
  /** Umbral mínimo confianza (0-100, default: 60) */
  minConfidence?: number;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

/** Resultado de búsqueda G10 R2 */
export interface DERAG10Result {
  /** Coordenada X UTM30 */
  x: number;
  /** Coordenada Y UTM30 */
  y: number;
  /** Confianza (0-100) */
  confidence: number;
  /** Precisión estimada metros */
  precision: number;
  /** Identificador de fuente */
  source: 'dera_g10_r2';
  /** Feature encontrada */
  feature: DERAG10Feature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES VALIDACIÓN
// =============================================================================

/** Rango válido X UTM30 Andalucía */
export const G10_X_RANGE = { min: 100_000, max: 700_000 };
/** Rango válido Y UTM30 Andalucía */
export const G10_Y_RANGE = { min: 3_950_000, max: 4_300_000 };
/** Precisión estimada DERA G10 (metros) — coordenadas oficiales */
export const G10_PRECISION_M = 10;
